import "../../index.css";
import getMonthName from "../../utils/getMonthName";
import getCurrentYear from "../../utils/getCurrentYear";
import useDateRangeLogic from "../../hooks/useDateRangeLogic";
import months from "../../utils/monthsList";
import ChevronButton from "../ChevronBtn";
import PoleSvg from "../PoleSvg";

export interface DateRangePickerProps {
  onRangeChange?: (range: { start: Date | null; end: Date | null }) => void;
  disabledDates?: Date[];
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  visibleMonths?: number;
  customDaysOfWeek?: string[];
  preSelectedRange?: {
    start: Date | null;
    end: Date | null;
  };
  startDateStyle?: string;
  endDateStyle?: string;
  inRangeStyles?: string;
  visibleDatesStyle?: string;
  daysOfWeekStyles?: string;
  monthTitleStyle?: string;
  showYearMonthDivider?: boolean;
  outlineStyle?: string;
  containerStyle?: string;
  prevMonthBtnIcon?: React.ReactNode;
  nextMonthBtnIcon?: React.ReactNode;
}

const DatePicker = ({
  onRangeChange,
  disabledDates = [],
  weekStartsOn = 0,
  visibleMonths = 2,
  customDaysOfWeek,
  preSelectedRange,
  startDateStyle,
  endDateStyle,
  inRangeStyles,
  visibleDatesStyle,
  daysOfWeekStyles,
  monthTitleStyle,
  showYearMonthDivider = true,
  outlineStyle,
  containerStyle,
  prevMonthBtnIcon,
  nextMonthBtnIcon,
}: DateRangePickerProps) => {
  const dateRangeLogic = useDateRangeLogic({
    onRangeChange,
    disabledDates,
    weekStartsOn,
    visibleMonths,
    customDaysOfWeek,
    preSelectedRange,
  });
  const outline = outlineStyle ?? "outline-blue-500";

  return (
    <div
      className={containerStyle ?? `py-4 px-2 border rounded shadow-md w-fit`}
    >
      <div className="flex justify-between items-center mb-4">
        {!prevMonthBtnIcon ? (
          <ChevronButton
            onClick={dateRangeLogic.handlePreviousMonth}
            direction="left"
            extraStyles={outline}
          />
        ) : (
          <button onClick={dateRangeLogic.handlePreviousMonth}>
            {prevMonthBtnIcon}
          </button>
        )}

        {/* Month range */}
        <div className="flex gap-2 items-center">
          <div className="block relative">
            <input
              ref={dateRangeLogic.inputRef}
              type="text"
              value={dateRangeLogic.monthsRange}
              className={`text-center border ${
                visibleMonths > 1 ? "w-40" : "w-24"
              } text-sm ${outline} rounded py-2 cursor-pointer`}
              readOnly
              onClick={() => {
                dateRangeLogic.setIsMonthDropdownVisible(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  dateRangeLogic.setIsMonthDropdownVisible(true);
                }
              }}
              // style={{ boxShadow: "0px 0px 0px 2px rgba(177, 231, 177, 0.64)" }}
            />
            {dateRangeLogic.isMonthDropdownVisible && (
              <div
                style={{
                  animation: dateRangeLogic.isMonthDropdownVisible
                    ? "fadeInSlideIn 0.2s ease-out"
                    : "fadeOutSlideOut 0.2s ease-out",
                }}
                ref={dateRangeLogic.dropdownRef}
                className={`dropdown my-2 transition-all duration-200 ease-in-out absolute top-10 left-0 bg-white h-72 overflow-y-auto flex flex-col w-full shadow-xl`}
              >
                {dateRangeLogic.months.map((month, index) => (
                  <button
                    key={month}
                    className={`py-2 text-sm text-gray-700 hover:bg-slate-100 duration-200 ease-in-out transition-colors ${outline}`}
                    onClick={() => dateRangeLogic.handleMonthChange(index)}
                  >
                    {month}
                  </button>
                ))}
              </div>
            )}
          </div>

          {showYearMonthDivider && (
            <span>
              <PoleSvg />
            </span>
          )}

          {/* Years */}
          <input
            type="number"
            value={dateRangeLogic.yearInput}
            onChange={dateRangeLogic.handleYearInputChange}
            className={`w-20 p-2 text-sm border rounded text-center ${outline}`}
            placeholder="Year"
          />
        </div>
        {!nextMonthBtnIcon ? (
          <ChevronButton
            onClick={dateRangeLogic.handleNextMonth}
            direction="right"
            extraStyles={outline}
          />
        ) : (
          <button onClick={dateRangeLogic.handleNextMonth}>
            {nextMonthBtnIcon}
          </button>
        )}
      </div>
      <div className="flex gap-4">
        {Array.from({ length: visibleMonths }).map((_, index) => {
          const currentDate = dateRangeLogic.currentDate;
          const date = new Date(
            getCurrentYear(currentDate, index),
            currentDate.getMonth() + index > 11
              ? (currentDate.getMonth() + index) % 12
              : currentDate.getMonth() + index,
            1
          );
          const dates = dateRangeLogic.getCalendarDates(
            date.getMonth(),
            date.getFullYear(),
            weekStartsOn
          );

          return (
            <div
              key={date.toDateString()}
              className="w-80 transition-all duration-500 ease-in-out"
            >
              {visibleMonths > 1 && (
                <p className={monthTitleStyle ?? `text-center font-semibold`}>
                  {" "}
                  {getMonthName(months, currentDate.getMonth() + index)},{" "}
                  {getCurrentYear(currentDate, index)}
                </p>
              )}

              {/* Days of the week */}
              <div className="grid grid-cols-7 gap-1 my-2">
                {dateRangeLogic.daysOfWeek.map((day: string) => (
                  <div
                    key={day}
                    className={`text-center ${
                      daysOfWeekStyles ?? "font-medium text-black/70"
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-7 gap-y-2 text-black/55">
                {dates.map((date: Date) => {
                  const isCurrentMonth =
                    date.getMonth() ===
                    (currentDate.getMonth() + index > 11
                      ? (currentDate.getMonth() + index) % 12
                      : currentDate.getMonth() + index);
                  const isDisabled = dateRangeLogic.isDateDisabled(date);
                  const startDate = dateRangeLogic.isStartDate(date);
                  const endDate = dateRangeLogic.isEndDate(date);
                  const inRange = dateRangeLogic.isInRange(date);

                  const isFirstInRow = date.getDay() === weekStartsOn;
                  const isLastInRow = date.getDay() === (weekStartsOn + 6) % 7;
                  const midRangeClass = inRange
                    ? isFirstInRow
                      ? "rounded-l-full"
                      : isLastInRow
                      ? "rounded-r-full"
                      : ""
                    : "";

                  return (
                    <button
                      key={date.toISOString()}
                      className={`p-2 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:text-opacity-40 transition-all duration-500 ease-in-out text-center cursor-pointer ${outline} ${
                        startDate && isCurrentMonth
                          ? `${
                              startDateStyle ??
                              "bg-blue-500 text-white rounded-l-full"
                            }`
                          : endDate && isCurrentMonth
                          ? `${
                              endDateStyle ??
                              "bg-blue-500 text-white rounded-r-full"
                            }`
                          : inRange && isCurrentMonth
                          ? `${
                              inRangeStyles ?? "bg-blue-200 text-blue-800"
                            } ${midRangeClass}`
                          : isCurrentMonth
                          ? `${visibleDatesStyle ?? "hover:bg-blue-100"}`
                          : "invisible"
                      }`}
                      onClick={() => dateRangeLogic.handleDateClick(date)}
                      onMouseEnter={() =>
                        !isDisabled && dateRangeLogic.setHoveredDate(date)
                      }
                      onMouseLeave={() => dateRangeLogic.setHoveredDate(null)}
                      disabled={isDisabled}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DatePicker;
